/**
 * cookie-consent.js — Talimestari
 * GDPR-suostumusbanneri + settings-modaali.
 * Shopify Customer Privacy API + localStorage (365 pv).
 */

(() => {
  const STORAGE_KEY    = 'talimestari_cookie_consent';
  const CONSENT_DAYS   = 365;
  const CONSENT_MS     = CONSENT_DAYS * 24 * 60 * 60 * 1000;

  // ── Elementit ──────────────────────────────────────────
  const banner   = document.getElementById('cookie-banner');
  const modal    = document.getElementById('cookie-modal');
  if (!banner || !modal) return;

  const overlay          = document.getElementById('cm-overlay');
  const analyticsToggle  = document.getElementById('cm-analytics');
  const marketingToggle  = document.getElementById('cm-marketing');

  let previousFocus = null; // fokus palautetaan tähän modaalin sulkeutuessa

  // ── LocalStorage ────────────────────────────────────────
  function getStoredConsent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const stored = JSON.parse(raw);
      if (!stored.timestamp) return null;
      if (Date.now() - new Date(stored.timestamp).getTime() > CONSENT_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return stored;
    } catch (e) {
      return null;
    }
  }

  function storeConsent(analytics, marketing) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      analytics,
      marketing,
      timestamp: new Date().toISOString()
    }));
  }

  // ── Google Consent Mode (gtag) ──────────────────────────
  function applyGtagConsent(analytics, marketing) {
    if (typeof gtag !== 'function') return;
    gtag('consent', 'update', {
      'analytics_storage':   analytics ? 'granted' : 'denied',
      'ad_storage':          marketing ? 'granted' : 'denied',
      'ad_user_data':        marketing ? 'granted' : 'denied',
      'ad_personalization':  marketing ? 'granted' : 'denied'
    });
  }

  // ── Shopify Customer Privacy API ────────────────────────
  function applyShopifyConsent(analytics, marketing) {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        window.Shopify.customerPrivacy.setTrackingConsent({
          analytics,
          marketing,
          preferences: true,
          sale_of_data: analytics && marketing
        }, function (error) {
          if (error) console.warn('[CookieConsent] Shopify API error:', error);
        });
      }
    } catch (e) {
      console.warn('[CookieConsent] Shopify API unavailable:', e);
    }
  }

  // ── Banner ────────────────────────────────────────────────
  function showBanner() {
    banner.removeAttribute('hidden');
    requestAnimationFrame(() => banner.setAttribute('data-visible', ''));
    // Fokus ensimmäiseen painikkeeseen (saavutettavuus)
    const firstBtn = banner.querySelector('button');
    if (firstBtn) firstBtn.focus();
  }

  function hideBanner() {
    banner.removeAttribute('data-visible');
    banner.addEventListener('transitionend', () => banner.setAttribute('hidden', ''), { once: true });
  }

  // ── Modaali ───────────────────────────────────────────────
  function openModal() {
    // Synkronoi togglet tallennettuun tilaan
    const stored = getStoredConsent();
    if (analyticsToggle) {
      analyticsToggle.checked = stored ? stored.analytics : false;
      analyticsToggle.setAttribute('aria-checked', analyticsToggle.checked);
    }
    if (marketingToggle) {
      marketingToggle.checked = stored ? stored.marketing : false;
      marketingToggle.setAttribute('aria-checked', marketingToggle.checked);
    }

    previousFocus = document.activeElement;
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => modal.setAttribute('data-visible', ''));

    // Fokus sulje-painikkeeseen
    const closeBtn = document.getElementById('cm-close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    modal.removeAttribute('data-visible');
    document.body.style.overflow = '';
    modal.addEventListener('transitionend', () => modal.setAttribute('hidden', ''), { once: true });
    if (previousFocus) previousFocus.focus();
  }

  // ── Suostumuksen tallentaminen ────────────────────────────
  function acceptAll() {
    storeConsent(true, true);
    applyGtagConsent(true, true);
    applyShopifyConsent(true, true);
    hideBanner();
    closeModal();
  }

  function rejectAll() {
    storeConsent(false, false);
    applyGtagConsent(false, false);
    applyShopifyConsent(false, false);
    hideBanner();
    closeModal();
  }

  function saveSettings() {
    const analytics = analyticsToggle ? analyticsToggle.checked : false;
    const marketing = marketingToggle ? marketingToggle.checked : false;
    storeConsent(analytics, marketing);
    applyGtagConsent(analytics, marketing);
    applyShopifyConsent(analytics, marketing);
    hideBanner();
    closeModal();
  }

  // ── Focus trap ────────────────────────────────────────────
  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    container.addEventListener('keydown', function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
      if (container.hasAttribute('hidden')) container.removeEventListener('keydown', handler);
    });
  }

  // ── Event listeners ───────────────────────────────────────

  // Banner
  document.getElementById('cb-accept-all')?.addEventListener('click', acceptAll);
  document.getElementById('cb-reject-all')?.addEventListener('click', rejectAll);
  document.getElementById('cb-open-modal')?.addEventListener('click', () => { openModal(); trapFocus(modal.querySelector('.cm__box')); });

  // Modaali
  document.getElementById('cm-accept-all')?.addEventListener('click', acceptAll);
  document.getElementById('cm-reject-all')?.addEventListener('click', rejectAll);
  document.getElementById('cm-save')?.addEventListener('click', saveSettings);
  document.getElementById('cm-close')?.addEventListener('click', closeModal);
  overlay?.addEventListener('click', closeModal);

  // Toggle aria-checked synkronointi
  [analyticsToggle, marketingToggle].forEach(toggle => {
    toggle?.addEventListener('change', () => {
      toggle.setAttribute('aria-checked', toggle.checked);
    });
  });

  // Esc sulkee modaalin
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
  });

  // Footer-linkki
  document.querySelectorAll('[data-open-cookie-modal]').forEach(el => {
    el.addEventListener('click', e => {
      e.preventDefault();
      openModal();
      trapFocus(modal.querySelector('.cm__box'));
    });
  });

  // ── Alustus ───────────────────────────────────────────────
  const stored = getStoredConsent();
  if (!stored) {
    // Ei aiempaa suostumusta — näytä banneri
    window.addEventListener('DOMContentLoaded', showBanner);
    if (document.readyState === 'interactive' || document.readyState === 'complete') showBanner();
  } else {
    // Aiempi suostumus olemassa — aktivoi tracking hiljaisesti
    applyGtagConsent(stored.analytics, stored.marketing);
    applyShopifyConsent(stored.analytics, stored.marketing);
  }

})();
